// ItineraryActions.js - Core business logic for itinerary operations
import { generateId, generatePoiId, validatePoi, initialPoiState } from '../ItineraryCreator/utils/utils';
import { normalizeBuilderTab, replaceWorkspaceUrl } from '../utils/builderUrlState';

export class ItineraryActions {
  constructor(dependencies) {
    this.itineraryState = dependencies.itineraryState;
    this.uiState = dependencies.uiState;
    this.draftState = dependencies.draftState;
    this.fileOperations = dependencies.fileOperations;
  }

  // Create new guide
  handleCreateNew = () => {
    const { resetToInitialState } = this.itineraryState;
    const { setCurrentStep, setActiveTab } = this.uiState;
    const { setAutoSaveDraftId } = this.draftState;

    resetToInitialState();
    setCurrentStep(2);
    setActiveTab('metadata');
    replaceWorkspaceUrl({ section: 'builder', guideUrl: null, guideId: null, tab: 'metadata' });
    // Reset auto-save draft ID for new itinerary
    setAutoSaveDraftId(null);
  };

  // Add new POI to itinerary
  handleAddPoi = async (options = {}) => {
    const keepEditing = Boolean(options?.keepEditing);
    const { currentPoi, addPoi, setCurrentPoi, setErrors, setHasUnsavedChanges, setOriginalPoi, language } = this.itineraryState;
    const { setActiveTab } = this.uiState;

    const validationErrors = validatePoi(currentPoi);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const timestamp = Date.now();
      const { itinerary } = this.itineraryState;
      const stopNumber = itinerary.points.length > 0
        ? Math.max(...itinerary.points.map(point => {
            const stopNumberPart = point.id.split('_')[1];
            return stopNumberPart && !isNaN(parseInt(stopNumberPart)) ? parseInt(stopNumberPart) : 0;
          })) + 1
        : 1;
      // Extract only the first 2 characters for QR code format (e.g., "pt-pt" -> "pt", "en-US" -> "en")
      const fullLanguageCode = itinerary.metadata.language || 'en';
      const languageCode = fullLanguageCode.split(/[-_]/)[0].toLowerCase();
      const id = `${timestamp}_${stopNumber}_${languageCode}`;

      const newPoi = {
        ...currentPoi,
        id: id,
        language: currentPoi.language || language
      };

      addPoi(newPoi);
      await this.fileOperations.saveItineraryToFile(itinerary);
      if (!keepEditing) {
        setCurrentPoi(initialPoiState);
      }
      setErrors({});
      setHasUnsavedChanges(false);
      setOriginalPoi(null);
      if (!keepEditing) {
        setActiveTab('map');
      }
    } catch (error) {
      console.error('Error adding POI:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message
      }));
    }
  };

  // Update existing POI
  handleUpdatePoi = async (options = {}) => {
    const keepEditing = Boolean(options?.keepEditing);
    const { currentPoi, updatePoi, setCurrentPoi, setErrors, setHasUnsavedChanges, setOriginalPoi, language, itinerary } = this.itineraryState;
    const { setActiveTab } = this.uiState;

    const validationErrors = validatePoi(currentPoi);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const updatedPoi = {
        ...currentPoi,
        id: currentPoi.id || generatePoiId(itinerary.metadata, itinerary.points),
        language: currentPoi.language || language,
        ttsVoice: currentPoi.ttsVoice || 'en-US-Studio-O',
      };

      updatePoi(currentPoi.id, updatedPoi);
      await this.fileOperations.saveItineraryToFile(itinerary);
      if (!keepEditing) {
        setCurrentPoi(initialPoiState);
      }
      setErrors({});
      setHasUnsavedChanges(false);
      setOriginalPoi(null);
      if (!keepEditing) {
        setActiveTab('map');
      }
    } catch (error) {
      console.error('Error updating POI:', error);
      setErrors(prev => ({
        ...prev,
        update: 'Failed to update POI'
      }));
    }
  };

  // Edit POI - prepare for editing
  handleEditPoi = (poi) => {
    const { setCurrentPoi, setIsUpdateMode, setOriginalPoi, setHasUnsavedChanges } = this.itineraryState;
    const { setActiveTab } = this.uiState;

    setCurrentPoi(poi);
    setIsUpdateMode(true);
    setOriginalPoi(JSON.parse(JSON.stringify(poi))); // Deep copy for comparison
    setHasUnsavedChanges(false); // Reset since we're loading fresh data
    setActiveTab('edit-poi');
  };

  // Create new POI
  handleNewPoi = () => {
    const { setCurrentPoi, setIsUpdateMode, setOriginalPoi, setHasUnsavedChanges } = this.itineraryState;
    const { setActiveTab } = this.uiState;

    setCurrentPoi(initialPoiState);
    setIsUpdateMode(false);
    setOriginalPoi(null);
    setHasUnsavedChanges(false);
    setActiveTab('edit-poi');
  };

  // Remove POI from itinerary
  handleRemovePoi = async (poiId) => {
    const { removePoi, itinerary } = this.itineraryState;

    if (window.confirm('Are you sure you want to remove this POI?')) {
      removePoi(poiId);
      if (this.fileOperations) {
        await this.fileOperations.saveItineraryToFile(itinerary);
      }
    }
  };

  // Reorder POIs
  handleReorderPOIs = (dragIndex, dropIndex) => {
    const { reorderPois } = this.itineraryState;
    reorderPois(dragIndex, dropIndex);
  };

  // Handle POI changes during editing
  handlePoiChange = (updatedPoi) => {
    const { setCurrentPoi, setHasUnsavedChanges, originalPoi, updatePoi } = this.itineraryState;

    setCurrentPoi(updatedPoi);

    // If this is an existing POI (has an ID), auto-update it in the main itinerary
    if (updatedPoi.id) {
      updatePoi(updatedPoi.id, updatedPoi);
    }

    // Check if POI has changed from original to set unsaved flag
    if (originalPoi && JSON.stringify(updatedPoi) !== JSON.stringify(originalPoi)) {
      setHasUnsavedChanges(true);
    } else if (!originalPoi) {
      // New POI, check if it has any content
      const hasContent = Object.values(updatedPoi).some(value =>
        value && value !== '' && (Array.isArray(value) ? value.length > 0 : true)
      );
      setHasUnsavedChanges(hasContent);
    }
  };

  // Load guide from listing
  handleLoadGuideFromListing = (guideData, databaseId = null, sourceUrl = '', requestedTab = 'metadata') => {
    const { setItinerary, setCurrentPoi, resetToInitialState, setSourceGuideUrl } = this.itineraryState;
    const { setCurrentStep, setActiveTab } = this.uiState;

    try {
      if (guideData.metadata && guideData.points) {
        // Check if ID needs to be fixed
        const jsonId = guideData.metadata.id;
        const needsIdFix = databaseId && jsonId !== databaseId;

        if (needsIdFix) {
          const shouldFix = window.confirm(
            `ID mismatch detected!\n\n` +
            `Database ID: ${databaseId}\n` +
            `JSON ID: ${jsonId || 'undefined'}\n\n` +
            `Would you like to fix the JSON ID to match the database ID?`
          );

          if (shouldFix) {
            // Update the guide data with the correct ID
            guideData = {
              ...guideData,
              metadata: {
                ...guideData.metadata,
                id: databaseId
              }
            };
            console.log(`Fixed guide ID from "${jsonId}" to "${databaseId}"`);
          }
        }

        setItinerary(guideData);
        setSourceGuideUrl(sourceUrl || '');
        setCurrentPoi(initialPoiState);
        setCurrentStep(2);
        const nextTab = normalizeBuilderTab(requestedTab);
        setActiveTab(nextTab);
        if (sourceUrl) {
          replaceWorkspaceUrl({
            section: 'builder',
            guideUrl: sourceUrl,
            guideId: databaseId || guideData.metadata?.id || '',
            tab: nextTab,
          });
        }
      } else {
        resetToInitialState();
        console.error('Invalid guide data format');
      }
    } catch (error) {
      console.error('Error loading guide:', error);
      resetToInitialState();
    }
  };

  handleLoadVenueFromListing = (venueData, sourceUrl = '') => {
    const { setItinerary, setCurrentPoi, resetToInitialState, setSourceGuideUrl } = this.itineraryState;
    const { setCurrentStep, setActiveTab } = this.uiState;

    try {
      if (!venueData || typeof venueData !== 'object') {
        resetToInitialState();
        console.error('Invalid venue data format');
        return;
      }

      const venueId = String(
        venueData.qrCodeId ||
        venueData.qr_code ||
        venueData.museumId ||
        venueData.id ||
        ''
      ).trim();

      const venuePage = {
        ...venueData,
        qrCodeId: venueId,
        guides: Array.isArray(venueData.guides) ? venueData.guides : []
      };

      const venueItinerary = {
        metadata: {
          id: venueId || '',
          name: venueData.displayName || venueData.museumName || 'Venue',
          description: venueData.welcomeMessage || 'Venue landing page',
          filename: venueId ? `${venueId}.json` : '',
          editor_mode: 'venue',
          venue_page_id: venueId || '',
          venue_page: venuePage,
          country: 'venue-pages'
        },
        points: []
      };

      setItinerary(venueItinerary);
      setSourceGuideUrl(sourceUrl || '');
      setCurrentPoi(initialPoiState);
      setCurrentStep(2);
      setActiveTab('venue-page');
    } catch (error) {
      console.error('Error loading venue:', error);
      resetToInitialState();
    }
  };

  // Step navigation with unsaved changes handling
  handleStepChange = async (stepIndex) => {
    const { hasUnsavedChanges, setHasUnsavedChanges, setOriginalPoi, isUpdateMode, currentPoi } = this.itineraryState;
    const { activeTab, setCurrentStep } = this.uiState;

    // Check for unsaved changes in edit-poi tab before navigation
    if (hasUnsavedChanges && activeTab === 'edit-poi' && (!isUpdateMode || !currentPoi?.id)) {
      const confirmSwitch = window.confirm(
        'You have unsaved changes to the current POI. Do you want to:\n\n' +
        '• Click "OK" to auto-save as temporary draft and continue\n' +
        '• Click "Cancel" to stay and manually save\n\n' +
        'Note: Auto-save will create a temporary draft without affecting your main guide.'
      );

      if (confirmSwitch) {
        // Auto-save as temporary draft
        const saveSuccess = await this.autoSaveAsDraft();

        if (saveSuccess) {
          // Clear unsaved changes flag and proceed with navigation
          setHasUnsavedChanges(false);
          setOriginalPoi(null);
          setCurrentStep(stepIndex);
          console.log('Changes auto-saved as temporary draft before step navigation');
        } else {
          // Auto-save failed, ask user what to do
          const forceSwitch = window.confirm(
            'Auto-save failed. Do you want to:\n\n' +
            '• Click "OK" to continue anyway (changes will be lost)\n' +
            '• Click "Cancel" to stay and try manual save'
          );

          if (forceSwitch) {
            setHasUnsavedChanges(false);
            setOriginalPoi(null);
            setCurrentStep(stepIndex);
          }
        }
        return;
      } else {
        // User chose to cancel, stay on current step
        return;
      }
    }

    // No unsaved changes or not on edit-poi tab, safe to navigate
    setCurrentStep(stepIndex);
  };

  // Auto-save as draft (placeholder - would need draft system integration)
  autoSaveAsDraft = async () => {
    // This would integrate with the draft system
    // For now, return true to indicate success
    return true;
  };
}
